<template>
  <div>
    <BarcodeScanner @scan="onBarcodeScan" v-model:is-scanning="isScanning" />
    <div class="flex p-2 md:p-3">
      <div class="flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3 p-2 bg-white flex-1 rounded-xl">
        <!-- Register -->
        <div class="flex items-center flex-shrink-0">
          <div class="bg-[#F8F8F8] flex items-center px-2 md:px-3 rounded-xl w-fit">
            <div class="flex flex-col items-center py-2 md:py-4 cursor-pointer"
              @click="$router.push($Route.ACCOUNTING_REGISTERS_REGISTER.replace(':id', $ctx.currentRegister.id))">
              <font-awesome-icon icon="cash-register" class="text-xl md:text-3xl" />
              <div v-if="$ctx.currentUser?.isOwner && $ctx.currentRegister?.id"
                class="text-xs text-[#6D6D6D] whitespace-nowrap truncate max-w-[160px] mt-2"
                :title="locale == 'ar' ? $ctx.currentRegister.arName : $ctx.currentRegister.enName">
                {{ locale == 'ar' ? $ctx.currentRegister.arName : $ctx.currentRegister.enName }}
              </div>
            </div>
            <div class="flex flex-col ms-1 md:ms-2 -space-y-1">
              <div v-if="registerLoading" class="flex justify-center items-center px-2">
                <loading-spinner diameter="20px" />
              </div>
              <template v-else-if="registerStatus?.status === 'close'">
                <div class="bg-red-100 text-red-800 font-bold text-xs px-2 py-1 rounded">{{ t('closed') }}</div>
              </template>
              <template v-else-if="registerStatus?.status === 'open'">
                <div class="flex items-baseline gap-1">
                  <div class="text-[#6D6D6D] text-xs md:text-base font-extrabold">IQD</div>
                  <div class="font-bold text-xs md:text-sm xl:text-lg bg-clip-text text-transparent"
                    style="background-image: linear-gradient(to right, #0B1F3B, #2A4B72)">
                    {{ formatMoney(registerStatus?.totalCashInAmount - registerStatus?.totalCashOutAmount + registerStatus?.openingFloatingAmount, 'IQD').replace('IQD', '') }}
                  </div>
                </div>
                <div class="flex items-baseline gap-1">
                  <div class="text-[#6D6D6D] text-xs md:text-base font-extrabold">USD</div>
                  <div class="font-bold text-xs md:text-sm xl:text-lg bg-clip-text text-transparent"
                    style="background-image: linear-gradient(to right, #0B1F3B, #2A4B72)">
                    {{ formatMoney(registerStatus?.foreignCurrencyTotalCashInAmount - registerStatus?.foreignCurrencyTotalCashOutAmount, 'USD').replace('$', '') }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <!-- Clock + Search (center) -->
        <div class="w-full order-last md:order-none md:flex-1 md:min-w-0 flex flex-col gap-1 justify-center items-center">
          <div dir="ltr" class="flex items-baseline gap-2">
            <div class="flex bg-clip-text text-transparent"
              style="background-image: linear-gradient(to right, #0B1F3B, #2A4B72)">
              <div class="font-bold text-base md:text-lg lg:text-xl w-[70px] md:w-[72px] lg:w-[82px]">{{ formatDate(now, 'hh:mm:ss') }}</div>
              <span class="font-bold text-base md:text-lg lg:text-xl">{{ formatDate(now, 'A') }}</span>
            </div>
            <div class="hidden lg:flex items-center rtl:flex-row-reverse gap-2 text-[#BDC6D7] text-sm">
              <div>{{ t(formatDate(now, 'dddd')) }}</div>
              <div>{{ formatDate(now, 'DD/MM/YYYY') }}</div>
            </div>
          </div>
          <div class="w-full md:max-w-sm lg:max-w-md">
            <GlobalSearch ref="globalSearchRef" />
          </div>
        </div>
        <!-- Logo + User (right) -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <img :src="settingsStore.getValue('company_logo') ?
            settingsStore.storagePath + '/' + settingsStore.getValue('company_logo') : '/images/logo.svg'"
            alt="" class="rounded-lg flex-shrink-0 w-8 h-8 object-contain lg:w-[40px] lg:h-[40px]" />
          <StoreSwitcher>
            <div class="text-sm lg:text-base font-bold bg-clip-text text-transparent whitespace-nowrap"
              style="background-image: linear-gradient(to right, #0B1F3B, #2A4B72)">
              <template v-if="locale == 'en'">{{ location?.enName }}</template>
              <template v-if="locale == 'ar'">{{ location?.arName }}</template>
            </div>
          </StoreSwitcher>
          <NotificationBell v-if="settingsStore.getValue('enable_notification_module') == 'true'" class="flex-shrink-0" />
          <el-dropdown trigger="click" class="flex-shrink-0">
            <div class="flex items-center gap-1 cursor-pointer hover:opacity-80">
              <div class="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#E8EDF2] flex items-center justify-center overflow-hidden flex-shrink-0">
                <img v-if="user?.avatar" :src="settingsStore.storagePath + '/' + user.avatar" class="w-full h-full object-cover" alt="" />
                <font-awesome-icon v-else icon="user" class="text-sm text-[#2A4B72]" />
              </div>
              <div class="hidden md:flex flex-col min-w-0">
                <div class="text-xs md:text-sm font-bold bg-clip-text text-transparent truncate max-w-[80px] lg:max-w-[110px]"
                  style="background-image: linear-gradient(to right, #0B1F3B, #2A4B72)">
                  {{ user ? `${user.name}` : '' }}
                </div>
              </div>
              <Icon icon="mdi:chevron-down" class="text-xs text-[#6D6D6D]" />
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click.prevent="$router.push($Route.HR_EMPLOYEE_SCHEDULE)">{{ $t('views.hr.viewSchedule') }}</el-dropdown-item>
                <el-dropdown-item @click.prevent="showDrawer('clock-in-out')">{{ $t('views.hr.clockInOut') }}</el-dropdown-item>
                <el-dropdown-item @click.prevent="$router.push({ path: $Route.HR_TASKS, query: { my_tasks: 'true' } })">{{ $t('views.hr.myTasks') }}</el-dropdown-item>
                <el-dropdown-item divided @click.prevent="switchLanguage">
                  <Icon icon="mdi:translate" class="me-1" />{{ t('switchLanguage') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-11 content-center gap-6 px-5">
      <!-- shortcuts -->
      <div class="col-span-5">
        <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-3">
          <div v-for="element in list" :key="element">
            <action-button v-if="element.icon === 'icon-examination'" class="pt-2 pb-4" style="margin-top: 0"
              :title="$t(element.title)" @click="$router.push(element.route)" :isSuccess="element.isSuccess">
              <span class="lg:text-5xl">
                <i :class="element.icon" />
              </span>
            </action-button>
            <action-button v-else-if="element.icon === 'return-sale'" class="pt-2 pb-4" style="margin-top: 0"
              :title="$t(element.title)" @click="$router.push(element.route)" :isDanger="element.isDanger">
              <CustomIcon>
                <IReturnSale />
              </CustomIcon>
            </action-button>
            <action-button v-else-if="element.image" class="pt-2 pb-4" style="margin-top: 0"
              :title="$t(element.title)" @click="$router.push(element.route)"
              :isSuccess="element.isSuccess" :isDanger="element.isDanger">
              <img :src="element.image" alt="" class="h-12 mx-auto" />
            </action-button>
            <action-button v-else-if="element.iconify" class="pt-2 pb-4" style="margin-top: 0"
              :title="$t(element.title)" @click="$router.push(element.route)"
              :isSuccess="element.isSuccess" :isDanger="element.isDanger">
              <span class="lg:text-5xl flex items-center justify-center">
                <Icon :icon="element.iconify" />
              </span>
            </action-button>
            <action-button v-else class="pt-2 pb-4" style="margin-top: 0" :title="$t(element.title)"
              @click="() => { if (element.route == $Route.SALES_NEW_SALE) { newSale() } else { $router.push(element.route) } }"
              :faIcon="element.icon" :faPrefix="element.iconPrefix" :isSuccess="element.isSuccess"></action-button>
          </div>
        </div>
      </div>
      <div class="col-span-3 md:col-span-1 lg:col-span-3 flex flex-col">
        <div class="bg-white h-[280px] lg:h-full rounded-xl p-2">
          <v-chart :option="option2" autoresize />
        </div>
      </div>
      <div class="col-span-3 md:col-span-1 lg:col-span-3 flex flex-col">
        <div class="bg-white h-[280px] lg:h-full rounded-xl p-2">
          <div class="flex gap-3 items-center justify-center pt-2">
            <font-awesome-icon @click="switchChartView('salesRep')"
              :icon="$ctx.locale == 'en' ? 'chevron-left' : 'chevron-right'"
              :class="currentChartView == 'cashier' ? 'text-blue-700 cursor-pointer' : 'text-gray-300'" />
            <div class="text-sm xl:text-lg font-bold text-gray-600">
              {{ currentChartView == 'salesRep' ? t('salesBySalesRep') : t('salesByCashier') }}
            </div>
            <font-awesome-icon @click="switchChartView('cashier')"
              :icon="$ctx.locale == 'en' ? 'chevron-right' : 'chevron-left'"
              :class="currentChartView == 'salesRep' ? 'text-blue-700 cursor-pointer' : 'text-gray-300'" />
          </div>
          <v-chart :option="currentChartView == 'salesRep' ? option : cashierOption" autoresize />
        </div>
      </div>
    </div>

    <div class="px-5 pt-4">
      <!-- Space image + Summary cards in one row -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
        <!-- Spacer above image card at lg -->
        <div class="hidden lg:block lg:col-span-2"></div>
        <!-- Today's Summary header — aligned above the 4 sales cards only -->
        <div class="md:col-span-2 lg:col-span-4 flex gap-5 items-center mb-0">
          <font-awesome-icon @click="updateCurrentSalesSummary('yesterday')"
            :icon="$ctx.locale == 'en' ? 'chevron-left' : 'chevron-right'" :class="currentSalesSummary == 'today' ? 'text-blue-700 cursor-pointer' : 'text-gray-300'
              " />
          <div class="text-lg text-gray-600">
            {{ currentSalesSummary == 'today' ? t('todaySummary') : t('yesterdaySummary') }}
          </div>
          <font-awesome-icon @click="updateCurrentSalesSummary('today')"
            :icon="$ctx.locale == 'en' ? 'chevron-right' : 'chevron-left'" :class="currentSalesSummary == 'yesterday' ? 'text-blue-700 cursor-pointer' : 'text-gray-300'
              " />
        </div>
        <div class="md:col-span-2 lg:col-span-2 relative rounded-xl overflow-hidden h-[150px] flex flex-col justify-end px-5">
          <img class="absolute left-0 top-0 -z-10 w-full h-full object-cover" :src="homeVersionCardImage" alt="" />
          <div class="flex items-center mt-1 pb-2">
            <div class="text-xl font-light tracking-[10px] text-white">limitless</div>
            <div class="ms-5 text-white font-bold">v1.10</div>
          </div>
        </div>
        <div v-for="(ssv, i) in salesSummaryValues" :key="i" class="rounded-xl h-[150px] flex flex-col p-3 md:p-4 lg:p-5"
          :style="{ backgroundColor: ssv.backgroundColor }">
          <div class="flex items-center gap-2">
            <div class="p-1.5 md:p-2 flex items-center justify-center rounded-full flex-shrink-0"
              :style="{ backgroundColor: ssv.iconBackgroundColor }">
              <font-awesome-icon :icon="ssv.icon" />
            </div>
            <div class="text-xs md:text-sm lg:text-sm xl:text-xl font-light mt-1 leading-tight">{{ ssv.name }}</div>
          </div>
          <div class="flex items-center justify-center text-sm md:text-base lg:text-xl xl:text-2xl font-bold flex-grow">
            {{ ssv.value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import ActionButton from '@/components/HomeActionButton.vue'
import { SalesSummary } from './reports/sales/SalesSummary.vue'
import { useHTTP } from '@/plugins/http'
import dayjs from 'dayjs'
import { useDrawer } from '@/plugins/drawer/drawer'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'
import { formatMoney } from '@/utils/money'

import CustomIcon from '@/components/CustomIcon.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Icon } from '@iconify/vue'
import IReturnSale from '@/components/icons/sales/IReturnSale.vue'
import { ALL_BUTTONS, buttonFlagsEnabled } from '@/composables/useHomeScreen'
import GlobalSearch from '@/components/GlobalSearch.vue'
import BarcodeScanner from '@/views/sales/BarcodeScanner.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import StoreSwitcher from '@/components/StoreSwitcher.vue'
import homeVersionCardImage from '@/assets/images/home-bg.png'
import { formatDate } from '@/utils/date'
import { RegisterStatus } from '@/models/company/Register'
import { Machine } from '@/models/company/Machine'
import { handleNewSale } from "@/lib/utils";
import { useSettingsStore } from "@/stores/settingsStore";
import { chartFontFamily } from "@/config/fonts";
import cheatbeep from "@/assets/sounds/cheat.mp3";
import { useHomeScreen } from '@/composables/useHomeScreen';

export default defineComponent({
  setup() {
    const { showDrawer } = useDrawer()
    const { t, locale } = useI18n({
      messages: {
        en: {
          shortcuts: 'Shortcuts',
          reports: 'Important Reports',
          salesBySalesRep: 'Sales By Sales Rep',
          Sunday: 'Sunday',
          Monday: 'Monday',
          Tuesday: 'Tuesday',
          Wednesday: 'Wednesday',
          Thursday: 'Thursday',
          Friday: 'Friday',
          Saturday: 'Saturday',
          totalinRegister: 'Total In Register',
          todaySummary: 'Today’s Summary',
          yesterdaySummary: 'Yesterday’s Summary',
          salesSummary: 'Sales Summary',
          numberOfSales: '# Of Sales',
          numberOfReturns: '# Of Returns',
          sales: 'Sales',
          cashIn: 'Cash In',
          moneyIn: 'Money In',
          orders: 'Orders',
          itemsSold: 'Items Sold',
          salesByCashier: 'Sales By Cashier',
          closed: 'Closed',
          switchLanguage: 'العربية'
        },
        ar: {
          shortcuts: 'مختصراتي',
          reports: 'التقارير المهمة',
          salesBySalesRep: 'المبيعات حسب ممثل البيع',
          Sunday: 'الأحد',
          Monday: 'الاثنين',
          Tuesday: 'الثلاثاء',
          Wednesday: 'الأربعاء',
          Thursday: 'الخميس',
          Friday: 'الجمعة',
          Saturday: 'السبت',
          totalinRegister: 'النقد في الصندوق',
          todaySummary: 'ملخص اليوم',
          yesterdaySummary: 'ملخص البارحة',
          salesSummary: 'نظرة عن المبيعات',
          numberOfSales: 'اجمالي المبيعات',
          numberOfReturns: 'اجمالي المسترجعات',
          sales: 'المبيعات',
          cashIn: 'النقد الداخل',
          moneyIn: 'المبلغ الداخل',
          orders: 'الفواتير',
          itemsSold: 'المنتجات المباعة',
          salesByCashier: 'المبيعات حسب الكاشير',
          closed: 'مغلق',
          switchLanguage: 'English'
        }
      }
    })

    const user = ref()
    const settingsStore = useSettingsStore()
    const location = ref()
    const http = useHTTP()
    const loading = ref(true)

    const option = ref({
      textStyle: { fontFamily: chartFontFamily },
      toolbox: {
        feature: {
          saveAsImage: {}
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: ({ seriesName, name, value, percent }: any) => {
          const salary = value ? formatMoney(value, 'IQD') : ''
          return `${name}</br>${salary} (${percent}%)`
        }
      },
      legend: {
        orient: 'vertical',
        bottom: 40,
        left: 'center',
        formatter: (data: any, x: any) => {
          return `${data}`
        }
      },
      series: [
        {
          name: t('salesBySalesRep'),
          type: 'pie',
          radius: '60%',
          center: ['50%', '40%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center'
          },
          data: [] as any[]
        }
      ]
    })

    const cashierOption = ref({
      textStyle: { fontFamily: chartFontFamily },
      toolbox: {
        feature: {
          saveAsImage: {}
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: ({ seriesName, name, value, percent }: any) => {
          const salary = value ? formatMoney(value, 'IQD') : ''
          return `${name}</br>${salary} (${percent}%)`
        }
      },
      legend: {
        orient: 'vertical',
        bottom: 40,
        left: 'center',
        formatter: (data: any, x: any) => {
          return `${data}`
        }
      },
      series: [
        {
          name: t('salesByCashier'),
          type: 'pie',
          radius: '60%',
          center: ['50%', '40%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center'
          },
          data: [] as any[]
        }
      ]
    })

    const option2 = ref({
      textStyle: { fontFamily: chartFontFamily },
      title: {
        text: t('salesSummary'),
        left: 'center'
      },
      toolbox: {
        feature: {
          saveAsImage: {}
        }
      },
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        orient: 'vertical',
        bottom: 40,
        left: 'center',
        formatter: (data: any, x: any) => {
          return `${data}`
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '23%',
        containLabel: true
      },

      xAxis: {
        type: 'category',
        boundaryGap: [0, 0.01],
        data: []
      },
      yAxis: {
        type: 'value',
        minInterval: 1
      },
      series: [
        {
          name: t('numberOfSales'),
          type: 'bar',
          barWidth: 40,
          color: '#27AE60',
          data: []
        },
        {
          name: t('numberOfReturns'),
          type: 'bar',
          barWidth: 40,
          color: '#F44336',
          data: []
        }
      ]
    })

    use([
      CanvasRenderer,
      PieChart,
      BarChart,
      TitleComponent,
      TooltipComponent,
      LegendComponent,
      GridComponent,
      ToolboxComponent,
      DataZoomComponent
    ])

    provide(THEME_KEY, 'light')

    const homeScreen = useHomeScreen()

    return {
      t,
      user,
      location,
      http,
      loading,
      locale,
      showDrawer,
      option,
      option2,
      cashierOption,
      homeVersionCardImage,
      formatDate,
      formatMoney,
      settingsStore,
      homeScreen
    }
  },

  components: {
    ActionButton,
    VChart,
    CustomIcon,
    IReturnSale,
    Icon,
    LoadingSpinner,
    GlobalSearch,
    BarcodeScanner,
    NotificationBell,
    StoreSwitcher
  },
  computed: {
    list() {
      const settingsStore = useSettingsStore()

      return this.homeScreen.activeButtonDefs.value.filter((btn: any) => {
        const hasPermission = this.$can(btn.permissionAction, btn.permissionResource)
        const flagOk = buttonFlagsEnabled(btn, key => settingsStore.getValue(key))
        return hasPermission && flagOk
      })
    }
  },
  data() {
    return {
      now: new Date(),
      text: '',
      keySequence: [] as string[],
      expectedSequence: ['Escape', 's', 'a', 'l', 'o', 'm', 'Enter'],
      salesSummary: new SalesSummary(),
      registerStatus: new RegisterStatus(),
      data: null,
      isScanning: false,
      registerLoading: true,
      currentChartView: localStorage.getItem('homeChartView') || 'salesRep',
      currentSalesSummary: 'today',
      salesSummaryValues: [
        {
          name: this.t('sales'),
          icon: 'dollar-sign',
          iconBackgroundColor: '#A3FFA1',
          backgroundColor: '#FFFFFF',
          value: null
        },
        {
          name: this.t('moneyIn'),
          icon: 'money-bill',
          iconBackgroundColor: '#F4F596',
          backgroundColor: '#FFFFFF',
          value: null
        },
        {
          name: this.t('orders'),
          icon: 'receipt',
          iconBackgroundColor: '#87E6F3',
          backgroundColor: '#FFFFFF',
          value: null
        },
        {
          name: this.t('itemsSold'),
          icon: 'box',
          iconBackgroundColor: '#ED94F4',
          backgroundColor: '#FFFFFF',
          value: null
        }
      ]
    }
  },

  async beforeMount() {
    const salesSummaryResp = await this.$http.get<SalesSummary>(
      `${SalesSummary.HOME_VERSION_ENDPOINT}?from_date=${dayjs().startOf('day')}&to_date=${dayjs().endOf('day')}`
    )

    this.salesSummary = SalesSummary.from(salesSummaryResp)
    this.salesSummary.summaryByNumberOfSales?.forEach((data) => {
      if (data.storeID == this.$ctx.currentLocation.id) {
        this.option2.series[0].data = [data.totalSales]
        this.option2.series[1].data = [data.totalReturns]
      }
    })

    const registerStatusRes = await this.$http.get<RegisterStatus>(
      `/company/registers/status?register_id=${this.$ctx.currentRegister.id}`
    )

    this.registerStatus = registerStatusRes
    this.registerLoading = false
  },

  mounted() {
    window.addEventListener('keyup', this.handleCheatKey)
    setInterval(() => (this.now = new Date()), 1000)
    this.user = JSON.parse(localStorage.getItem('user') || 'null')
    this.http
      .get(`/company/locations/${this.$ctx.currentLocation.id}`)
      .then((data) => {
        this.location = data as Location
        this.$ctx.setLocation(this.location)
      })
      .catch((e) => {
        this.$router.replace('login')
      })
    this.refreshRegisterName()

    this.loadSalesRep(new Date())
    this.loadCashierSales(new Date())
    this.loadSalesSummaryValues(new Date())
  },
  unmounted() {
    window.removeEventListener('keyup', this.handleCheatKey)
  },
  methods: {
    async refreshRegisterName() {
      const guid = this.$ctx.currentMachine?.machineGUID
      if (!guid) return
      try {
        const machine = await this.$http.get<Machine>(`${Machine.ENDPOINT}?machine_guid=${guid}`)
        const register = machine?.register
        if (!register?.id || register.id !== this.$ctx.currentRegister?.id) return
        this.$ctx.setRegister(Object.assign(this.$ctx.currentRegister, { enName: register.enName, arName: register.arName }))
      } catch {
        // keep the cached register
      }
    },
    switchLanguage() {
      const newLocale = this.$ctx.locale === 'ar' ? 'en' : 'ar'
      const setting = this.settingsStore.settings.find((s: any) => s.key === 'system_language')
      if (setting) {
        this.settingsStore.updateSetting('system_language', setting.id, newLocale)
      } else {
        this.$ctx.setLocale(newLocale)
        window.location.reload()
      }
    },
    loadSalesRep(date: Date) {
      const urlParams = new URLSearchParams()
      urlParams.set('from_date', dayjs(date).format('YYYY-MM-DD'))
      urlParams.set('to_date', dayjs(date).format('YYYY-MM-DD'))

      this.http.get<any[]>('/reports/home/top-sales-reps?' + urlParams.toString()).then((data) => {
        const chartData: any = []
        data.forEach((i: any) => {
          chartData.push({
            value: i.total,
            name: `${i.name}`
          })
        })

        this.option.series[0].data = chartData
      })
    },
    loadCashierSales(date: Date) {
      const urlParams = new URLSearchParams()
      urlParams.set('from_date', dayjs(date).format('YYYY-MM-DD'))
      urlParams.set('to_date', dayjs(date).format('YYYY-MM-DD'))

      this.http.get<any[]>('/reports/sales/top-reps?' + urlParams.toString()).then((data) => {
        const chartData: any = []
        data.forEach((i: any) => {
          chartData.push({
            value: i.total,
            name: `${i.name}`
          })
        })

        this.cashierOption.series[0].data = chartData
      })
    },
    switchChartView(value: string) {
      if (this.currentChartView === value) return
      this.currentChartView = value
      localStorage.setItem('homeChartView', value)
    },
    loadSalesSummaryValues(date: Date) {
      const urlParams = new URLSearchParams()
      urlParams.set('from_date', dayjs(date).format('YYYY-MM-DD'))
      urlParams.set('to_date', dayjs(date).format('YYYY-MM-DD'))

      this.http.get<any[]>('/reports/home/total-sales?' + urlParams.toString()).then((data) => {
        this.salesSummaryValues[0].value = this.formatMoney(data?.sales_amount, this.$ctx.currency)
        this.salesSummaryValues[1].value = this.formatMoney(data?.total_money_in, this.$ctx.currency)
        this.salesSummaryValues[2].value = data?.total_orders
        this.salesSummaryValues[3].value = data?.total_items_sold
      })
    },
    updateCurrentSalesSummary(value: string) {
      this.currentSalesSummary = value
      const urlParams = new URLSearchParams()
      const today = new Date()
      let date = null

      if (value == 'today') {
        date = today
      } else {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        date = yesterday
      }

      this.loadSalesSummaryValues(date)
    },
    handleCheatKey(event: KeyboardEvent) {
      this.keySequence.push(event.key)
      if (this.keySequence.length > this.expectedSequence.length) {
        this.keySequence.shift()
      }
      if (this.keySequence.join('') === this.expectedSequence.join('')) {
        const audio = new Audio(cheatbeep)
        audio.play()
        this.keySequence = []
        this.$router.push(this.$Route.DEV_TOOLS)
      }
    },
    onBarcodeScan(code: string) {
      this.http.get<any>(`/search/global?q=${encodeURIComponent(code)}`).then((data: any) => {
        if (data.results && data.results.length === 1) {
          this.$router.push(data.results[0].viewRoute)
        } else if (this.$refs.globalSearchRef) {
          (this.$refs.globalSearchRef as any).searchAndNavigate(code)
        }
      })
    },
    newSale() {
      handleNewSale(this.t);
      this.$router.push(this.$Route.SALES_NEW_SALE);
    }
  }
})
</script>

<style scoped>
.header {
  @apply flex p-4 m-3 rounded shadow-sm items-center justify-between;
  background: var(--secondary-bg-color);
  color: var(--secondary-text-color);
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.icon {
  @apply cursor-pointer text-2xl rounded-full h-12 w-12 flex items-center justify-center;
  background: var(--body-bg-color);
  color: var(--secondary-text-color);
}

.icon:hover {
  background: var(--primary-hover-bg-color);
  color: var(--primary-hover-text-color);
}

.title {
  @apply col-span-1 p-2 rounded shadow-sm text-center text-lg w-full;
  background: var(--secondary-bg-color);
  color: var(--secondary-text-color);
}

@screen lg {
  .title {
    @apply col-span-3;
  }
}
</style>
